import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  useTheme,
  useMediaQuery,
  LinearProgress,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GameScreenShell from "@components/Home/Games/GameScreenShell/GameScreenShell";
import SessionSummary, {
  SessionSummaryStats,
} from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useCreateSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
  useGetProgressQuery,
  useGetContentQuery,
} from "@store/gameApi";
import { RootState } from "@store/store";
import {
  getLevelParams,
  resolveRoundContent,
  type LevelParams,
  type RoundContent,
  type PatternMatchCard,
} from "./patternMatchContent";

export interface PatternMatchGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

const ROUNDS_PER_SESSION = 3;
const SESSION_START_DEDUPE_MS = 2000;
const FLIP_BACK_MS = 1200;
const MIN_CARD_PX = 80;

let lastSessionStartAt = 0;

type Phase = "idle" | "playing" | "feedback";
type CardState = "hidden" | "flipped" | "matched";

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

const PatternMatch: FC<PatternMatchGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId
  );
  const sessionStartedRef = useRef(false);
  const [createSession] = useCreateSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(
    null
  );
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();
  const { data: progressData } = useGetProgressQuery(
    { patientId: selectedPatientId! },
    { skip: !selectedPatientId }
  );

  const [startedGame, setStartedGame] = useState<GameSessionGame | null>(null);
  const gameFromSession = effectiveGame ?? startedGame ?? null;
  const contentSeed = gameFromSession?.contentSeed ?? "";
  const difficulty = gameFromSession
    ? Math.max(1, Math.min(10, gameFromSession.difficulty))
    : 1;
  const levelParams = useMemo<LevelParams>(
    () => getLevelParams(difficulty),
    [difficulty]
  );

  const contentSeedForFetch =
    contentSeed ||
    (effectiveSessionId
      ? `${effectiveSessionId}-${GAME_IDS.patternMatch}`
      : "");
  const { data: contentData, error: contentError } = useGetContentQuery(
    { gameId: GAME_IDS.patternMatch, seed: contentSeedForFetch },
    { skip: !contentSeedForFetch }
  );

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [roundCount, setRoundCount] = useState(0);
  const [roundContent, setRoundContent] = useState<RoundContent | null>(null);
  const [cardStates, setCardStates] = useState<CardState[]>([]);
  const [currentFlipped, setCurrentFlipped] = useState<number[]>([]);
  const [totalMoves, setTotalMoves] = useState(0);
  const [wrongMatches, setWrongMatches] = useState(0);
  const [moveHistory, setMoveHistory] = useState<number[]>([]);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [roundTimeRemaining, setRoundTimeRemaining] = useState(0);
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<
    { accuracy: number; completionMs: number }[]
  >([]);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<
    Record<string, number>
  >({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(
    null
  );
  const [muted, setMuted] = useState(false);
  const [imageLoadFailed, setImageLoadFailed] = useState<Set<string>>(new Set());
  const submittedForRoundRef = useRef(false);
  const sessionSeedRef = useRef<string | null>(null);
  const flipBackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const level = progressData?.level ?? 1;

  const sessionSeed = useMemo(() => {
    if (sessionSeedRef.current) return sessionSeedRef.current;
    sessionSeedRef.current = `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    return sessionSeedRef.current;
  }, []);

  const startNewRound = useCallback(
    (roundIndex?: number) => {
      submittedForRoundRef.current = false;
      if (flipBackTimeoutRef.current) {
        clearTimeout(flipBackTimeoutRef.current);
        flipBackTimeoutRef.current = null;
      }
      const idx = roundIndex ?? roundCount;
      const content = resolveRoundContent(
        contentSeed,
        idx,
        levelParams,
        contentData,
        contentError,
        sessionSeed
      );
      setRoundContent(content);
      setImageLoadFailed(new Set());
      setCardStates(content.cards.map(() => "hidden" as CardState));
      setCurrentFlipped([]);
      setTotalMoves(0);
      setWrongMatches(0);
      setMoveHistory([]);
      const now = Date.now();
      setRoundStartTime(now);
      setRoundTimeRemaining(
        levelParams.timeSeconds > 0 ? levelParams.timeSeconds : 0
      );
      setXpEarned(null);
      setPhase("playing");
    },
    [
      contentSeed,
      roundCount,
      levelParams,
      contentData,
      contentError,
      sessionSeed,
    ]
  );

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAt < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAt = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    createSession({
      patientId: selectedPatientId,
      sessionType: "freeplay",
      gameId: GAME_IDS.patternMatch,
    })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId) {
          setSessionId(res.sessionId);
          setSessionStartTime(Date.now());
          setStartedGame(res?.games?.[0] ?? null);
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [
    isGuided,
    isFreePlayFromLibrary,
    selectedPatientId,
    createSession,
    t,
  ]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(Date.now());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary]);

  useEffect(() => {
    if (sessionStartTime == null || paused) return;
    const id = setInterval(() => {
      setTimerSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, paused]);

  useEffect(() => {
    if (phase !== "playing" || paused || !roundContent) return;
    if (levelParams.timeSeconds <= 0) return;
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - roundStartTime) / 1000);
      const remaining = Math.max(0, levelParams.timeSeconds - elapsed);
      setRoundTimeRemaining(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 200);
    return () => clearInterval(interval);
  }, [
    phase,
    paused,
    roundContent,
    roundStartTime,
    levelParams.timeSeconds,
  ]);

  const submitCurrentRound = useCallback(() => {
    if (!roundContent || submittedForRoundRef.current) return;
    submittedForRoundRef.current = true;
    const correctMatches = totalMoves - wrongMatches;
    const accuracy =
      totalMoves > 0 ? correctMatches / totalMoves : 0;
    const completionTimeMs = Date.now() - roundStartTime;

    setRoundResults((prev) => [
      ...prev,
      { accuracy, completionMs: completionTimeMs },
    ]);

    if (sessionId) {
      submitRound({
        sessionId,
        body: {
          gameId: GAME_IDS.patternMatch,
          roundNumber: roundCount + 1,
          difficulty,
          accuracy,
          reactionTimeMs: null,
          completionTimeMs,
          errorCount: wrongMatches,
          rawData: {
            totalMoves,
            wrongMatches,
            completionTime: completionTimeMs,
            moveHistory,
          },
        },
      })
        .unwrap()
        .then((res) => {
          if (res?.xpEarned != null) {
            setXpEarned(res.xpEarned);
            setSessionScore((s) => s + res.xpEarned);
          }
          if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
            setDomainUpdatesSum((prev) => {
              const next = { ...prev };
              for (const [k, v] of Object.entries(res.domainUpdates)) {
                next[k] = (next[k] ?? 0) + v;
              }
              return next;
            });
          }
        })
        .catch(() => {});
    } else {
      const xp = accuracy >= 0.8 ? 20 : 10;
      setXpEarned(xp);
      setSessionScore((s) => s + xp);
    }
    setPhase("feedback");
  }, [
    roundContent,
    totalMoves,
    wrongMatches,
    roundStartTime,
    moveHistory,
    sessionId,
    roundCount,
    difficulty,
    submitRound,
  ]);

  const handleCardTap = useCallback(
    (index: number) => {
      if (phase !== "playing" || !roundContent) return;
      if (cardStates[index] === "matched" || cardStates[index] === "flipped")
        return;
      if (currentFlipped.length >= 2) return;

      const nextFlipped = [...currentFlipped, index];
      setCurrentFlipped(nextFlipped);
      setCardStates((prev) => {
        const next = [...prev];
        next[index] = "flipped";
        return next;
      });

      if (nextFlipped.length === 2) {
        const [a, b] = nextFlipped;
        const cardA = roundContent.cards[a];
        const cardB = roundContent.cards[b];
        const isMatch = cardA?.pairId === cardB?.pairId;

        setTotalMoves((m) => m + 1);
        setMoveHistory((h) => [...h, Date.now()]);

        if (isMatch) {
          setCardStates((prev) => {
            const next = [...prev];
            next[a] = "matched";
            next[b] = "matched";
            return next;
          });
          setCurrentFlipped([]);
        } else {
          setWrongMatches((w) => w + 1);
          flipBackTimeoutRef.current = setTimeout(() => {
            setCardStates((prev) => {
              const next = [...prev];
              next[a] = "hidden";
              next[b] = "hidden";
              return next;
            });
            setCurrentFlipped([]);
            flipBackTimeoutRef.current = null;
          }, FLIP_BACK_MS);
        }
      }
    },
    [phase, roundContent, currentFlipped]
  );

  const pairsFound = useMemo(() => {
    if (!roundContent) return 0;
    return cardStates.filter((s) => s === "matched").length / 2;
  }, [roundContent, cardStates]);

  const allMatched =
    roundContent &&
    cardStates.length === roundContent.cards.length &&
    cardStates.every((s) => s === "matched");

  useEffect(() => {
    if (phase === "playing" && allMatched && roundContent) {
      submitCurrentRound();
    }
  }, [phase, allMatched, roundContent, submitCurrentRound]);

  useEffect(() => {
    if (
      phase === "playing" &&
      levelParams.timeSeconds > 0 &&
      roundTimeRemaining <= 0 &&
      roundContent
    ) {
      submitCurrentRound();
    }
  }, [
    phase,
    levelParams.timeSeconds,
    roundTimeRemaining,
    roundContent,
    submitCurrentRound,
  ]);

  const handleNextRound = useCallback(() => {
    const nextRound = roundCount + 1;
    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      const allResults = [...roundResults];
      const totalRounds = allResults.length;
      const avgAccuracy =
        totalRounds > 0
          ? (allResults.reduce((s, r) => s + r.accuracy, 0) / totalRounds) * 100
          : 0;
      const avgCompletionSec =
        totalRounds > 0
          ? allResults.reduce((s, r) => s + r.completionMs, 0) /
            totalRounds /
            1000
          : 0;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgCompletionSec,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgCompletionSec,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: avgAccuracy,
          avgReactionSeconds: avgCompletionSec,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      lastSessionStartAt = 0;
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    setRoundCount(nextRound);
    startNewRound(nextRound);
  }, [
    roundCount,
    roundResults,
    sessionId,
    sessionScore,
    domainUpdatesSum,
    isGuided,
    onGuidedGameComplete,
    completeSession,
    startNewRound,
  ]);

  const handleExit = useCallback(() => {
    if (flipBackTimeoutRef.current) {
      clearTimeout(flipBackTimeoutRef.current);
    }
    if (sessionId) completeSession({ sessionId }).catch(() => {});
    lastSessionStartAt = 0;
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleSummaryContinue = useCallback(() => navigate(-1), [navigate]);

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "feedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  const feedbackText =
    phase === "feedback" ? t("games.patternMatch.feedbackGood") : null;

  const cardSize = useMemo(() => {
    const min = Math.max(levelParams.minCardSizePx, MIN_CARD_PX);
    const cols = levelParams.gridCols;
    const gap = 8;
    if (isMobile) {
      const maxW = (theme.breakpoints.values.sm ?? 600) - 32;
      const size = Math.floor((maxW - gap * (cols - 1)) / cols);
      return Math.max(min, Math.min(size, 100));
    }
    return Math.max(min, 90);
  }, [levelParams.minCardSizePx, levelParams.gridCols, isMobile, theme]);

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary
        stats={summaryStats}
        onContinue={handleSummaryContinue}
      />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.patternMatch.title")}
      level={level}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={() => setPaused((p) => !p)}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
    >
      {phase === "idle" && (
        <Box
          textAlign="center"
          py={4}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.patternMatch.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.patternMatch.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="medium"
            onClick={() => startNewRound()}
            sx={{ minHeight: 48, minWidth: 180, alignSelf: "center" }}
          >
            {t("games.patternMatch.startRound")}
          </Button>
        </Box>
      )}

      {(phase === "playing" || phase === "feedback") && roundContent && (
        <>
          {levelParams.timeSeconds > 0 && phase === "playing" && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={
                  levelParams.timeSeconds > 0
                    ? (roundTimeRemaining / levelParams.timeSeconds) * 100
                    : 100
                }
                color="primary"
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {t("games.patternMatch.timeRemaining", {
                  seconds: roundTimeRemaining,
                })}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("games.patternMatch.pairsFound", {
              count: pairsFound,
              total: levelParams.pairCount,
            })}
            {" · "}
            {t("games.patternMatch.moves", { count: totalMoves })}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${levelParams.gridCols}, ${cardSize}px)`,
              gridTemplateRows: `repeat(${levelParams.gridRows}, ${cardSize}px)`,
              gap: 1.5,
              justifyContent: "center",
              maxWidth: "100%",
              mx: "auto",
              mb: 2,
            }}
          >
            {roundContent.cards.map((card: PatternMatchCard, index: number) => (
              <CardCell
                key={card.id}
                card={card}
                state={cardStates[index] ?? "hidden"}
                size={cardSize}
                onClick={() => handleCardTap(index)}
                disabled={phase !== "playing" || currentFlipped.length >= 2}
                imageLoadFailed={imageLoadFailed}
                onImageError={() =>
                  setImageLoadFailed((prev) => new Set(prev).add(card.id))
                }
              />
            ))}
          </Box>

          {phase === "feedback" && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
              mt={2}
              mb={2}
            >
              {xpEarned != null && (
                <Typography variant="body2" color="primary.main">
                  +{xpEarned} XP
                </Typography>
              )}
              <Button
                variant="contained"
                size="medium"
                onClick={handleNextRound}
                sx={{ minHeight: 48, minWidth: 180 }}
              >
                {roundCount + 1 >= ROUNDS_PER_SESSION
                  ? t("games.patternMatch.finishSession")
                  : t("games.patternMatch.nextRound")}
              </Button>
            </Box>
          )}
        </>
      )}
    </GameScreenShell>
  );
};

interface CardCellProps {
  card: PatternMatchCard;
  state: CardState;
  size: number;
  onClick: () => void;
  disabled: boolean;
  imageLoadFailed: Set<string>;
  onImageError: () => void;
}

const CardCell: FC<CardCellProps> = ({
  card,
  state,
  size,
  onClick,
  disabled,
  imageLoadFailed,
  onImageError,
}) => {
  const showFront = state === "flipped" || state === "matched";
  const isMatched = state === "matched";

  return (
    <Box
      onClick={disabled ? undefined : onClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if (!disabled && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      }}
      aria-pressed={showFront}
      sx={{
        width: size,
        minWidth: size,
        height: size,
        minHeight: size,
        borderRadius: 2,
        perspective: 600,
        position: "relative",
        cursor: disabled ? "default" : "pointer",
        "&:hover .pattern-match-card-inner": disabled
          ? {}
          : { "&:not(.pattern-match-flipped)": { "& .pattern-match-back": { bgcolor: "action.selected" } } },
      }}
    >
      <Box
        className={`pattern-match-card-inner ${showFront ? "pattern-match-flipped" : ""}`}
        sx={{
          position: "absolute",
          inset: 0,
          transformStyle: "preserve-3d",
          transition: "transform 0.25s ease",
          transform: showFront ? "rotateY(180deg)" : "rotateY(0deg)",
          borderRadius: 2,
          border: "2px solid",
          borderColor: isMatched ? "success.main" : "divider",
          boxShadow: isMatched
            ? "0 0 0 2px rgba(76, 175, 80, 0.3)"
            : "none",
          animation: isMatched ? "pattern-match-pulse 1.5s ease-in-out infinite" : "none",
          "@keyframes pattern-match-pulse": {
            "0%, 100%": { boxShadow: "0 0 0 2px rgba(76, 175, 80, 0.3)" },
            "50%": { boxShadow: "0 0 0 4px rgba(76, 175, 80, 0.5)" },
          },
        }}
      >
        <Box
          className="pattern-match-back"
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: 2,
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            bgcolor: "action.hover",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "70%",
              height: "70%",
              borderRadius: 1,
              bgcolor: "primary.main",
              opacity: 0.4,
            }}
          />
        </Box>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: 2,
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
            bgcolor: "background.paper",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 0.5,
          }}
        >
          {card.imageUrl && !imageLoadFailed.has(card.id) ? (
            <Box
              component="img"
              src={
                card.imageUrl.startsWith("/")
                  ? process.env.PUBLIC_URL + card.imageUrl
                  : card.imageUrl
              }
              alt={card.label}
              onError={onImageError}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          ) : (
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontSize: "1rem" }}
            >
              {card.label?.slice(0, 1).toUpperCase() ?? "?"}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PatternMatch;
